// ST-1258 -Claude- Business detail page — Yelp-style.
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getBusiness } from '@nearrme/api-client';
import { EmptyState, LoadingSkeleton, Button, ProductTile } from '@nearrme/ui-web';
import { getApiClient } from '../api/client';

export function BusinessDetailPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const { t } = useTranslation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['business', businessId],
    queryFn: () => getBusiness(getApiClient(), Number(businessId)),
    enabled: !!businessId,
  });

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <LoadingSkeleton className="h-48 w-full mb-6" />
        <LoadingSkeleton className="h-8 w-2/3" />
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <EmptyState title={t('errors.notFound')} description="This store might be unavailable." />
      </div>
    );
  }

  const business = data.data;

  const directionsUrl =
    business.latitude && business.longitude
      ? `https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`
      : null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 animate-fade-in">
      {/* HERO */}
      <div className="rounded-2xl bg-gradient-to-br from-orange-100 to-blue-100 dark:from-orange-950 dark:to-blue-950 p-8 mb-6">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="w-24 h-24 rounded-2xl bg-white dark:bg-slate-900 flex items-center justify-center text-5xl shadow-md">
            🏪
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              {business.name}
            </h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {business.isAuthorizedDealer && (
                <span className="text-xs px-2 py-1 rounded-full bg-orange-500 text-white">
                  {t('business.authorizedDealer')}
                </span>
              )}
              {business.isServiceCenter && (
                <span className="text-xs px-2 py-1 rounded-full bg-purple-500 text-white">
                  {t('business.serviceCenter')}
                </span>
              )}
              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  business.isOpenNow ? 'bg-green-500 text-white' : 'bg-slate-500 text-white'
                }`}
              >
                {business.isOpenNow ? t('business.openNow') : t('business.closed')}
              </span>
            </div>
            <div className="mt-3 text-slate-700 dark:text-slate-300">
              {business.address && `${business.address}, `}
              {business.city}, {business.state}
              {business.pinCode && ` - ${business.pinCode}`}
            </div>
            {business.distanceKm != null && (
              <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                📍 {business.distanceKm.toFixed(1)} km away
              </div>
            )}
            <div className="mt-4 flex gap-3 flex-wrap">
              {directionsUrl && (
                <a href={directionsUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="primary">📍 {t('business.directions')}</Button>
                </a>
              )}
              {business.phone && (
                <a href={`tel:${business.phone}`}>
                  <Button variant="outline">📞 Call</Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PRODUCTS */}
      {business.productsAvailable.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4">
            {t('business.productsAvailable')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {business.productsAvailable.map((p) => (
              <ProductTile
                key={p.productId}
                product={p}
                onClick={(prod) => (window.location.href = `/p/${prod.productId}`)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
